import { CollabEventPresenceData, CursorSession } from '@livekatsomo/models';
import { getVersion } from 'prosemirror-collab';
import { EditorState, Transaction } from 'prosemirror-state';
import { Decoration } from 'prosemirror-view';
import { CustomPluginState } from './types';
import { updateOwnCursorChanges } from './updateOwnCursorChanges';
import { dimOverlappingSelections } from './dimOverlappingSelections';
import { adjustDecorationsOnDocumentChange } from './adjustDecorationsOnDocumentChange';
import { updateCursors } from './updateCursors';
import { updateParticipants } from './updateParticipants';
import { getUpdatedDecorations } from './getUpdatedDecorations';

/**
 * Applies a transaction to the plugin state and returns the updated state.
 * @param tr - The transaction to apply.
 * @param value - The current state of the plugin.
 * @param oldState - The previous state of the editor.
 * @param newState - The new state of the editor.
 * @returns The updated state of the plugin.
 */
export function applyTransaction(
  tr: Transaction,
  value: CustomPluginState,
  oldState: EditorState,
  newState: EditorState,
): CustomPluginState {
  updateOwnCursorChanges(
    newState,
    value.clientID,
    value.sendCurrentCursorPosition,
  );
  const { participants, queuedCursors } = value;
  let { decorationSet } = value;
  let version: number;
  try {
    version = getVersion(newState);
  } catch (err) {
    // Collab plugin not configured yet, ignore
    return value;
  }

  const presenceData = tr.getMeta('presence') as CollabEventPresenceData;
  const telepointerData = tr.getMeta('cursor') as CursorSession;
  const add: Decoration[] = [];
  const remove: Decoration[] = [];

  if (presenceData) {
    updateParticipants(presenceData, participants, decorationSet, remove);
  }

  if (telepointerData) {
    updateCursors({
      version,
      telepointerData,
      participants,
      value,
      decorationSet,
      remove,
      tr,
      add,
    });
  }

  if (value.queuedCursors.size) {
    for (const queuedCursor of value.queuedCursors.values()) {
      if (queuedCursor.version <= version) {
        value.queuedCursors.delete(queuedCursor.clientID);
        updateCursors({
          version,
          telepointerData: queuedCursor,
          participants,
          value,
          decorationSet,
          remove,
          tr,
          add,
        });
      }
    }
  }

  if (tr.docChanged) {
    decorationSet = adjustDecorationsOnDocumentChange({
      tr,
      decorationSet,
      add,
      participants,
      remove,
    });
  }

  dimOverlappingSelections(tr, decorationSet);

  decorationSet = getUpdatedDecorations({
    add,
    remove,
    decorationSet,
    tr,
  });

  return {
    participants,
    decorationSet: decorationSet,
    queuedCursors,
    clientID: value.clientID,
    listenToRemoteCursorUpdates: value.listenToRemoteCursorUpdates,
    removeCursorPositionOnDestroy: value.removeCursorPositionOnDestroy,
    sendCurrentCursorPosition: value.sendCurrentCursorPosition,
  };
}
